import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, message, Tooltip, Tabs, Space, Checkbox } from 'antd';
import { QuestionCircleOutlined, ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import {
  isTenantTeamPick,
  performTenantLogin,
  type TeamPickCandidate,
  type TenantLoginSuccess
} from '../services/tenantLogin';
import ContactForm from '../components/ContactForm';
import SiteBeianRecords from '../components/SiteBeianRecords';
import QuickWorkbenchModal from '../components/QuickWorkbenchModal';
import TeamPickModal from '../components/TeamPickModal';
import WechatWebBindModal from '../components/WechatWebBindModal';
import './HomePage.css';
import '../components/LoginModal.css';
import { HERO_BACKGROUND_MP4 } from '../constants/publicAssets';
import { resolvePostLoginPath } from '../platform-admin/staffConsolePaths';
import { loadSavedTenantLogin, persistTenantLoginIfRemembered } from '../utils/loginRemember';
import { ENABLE_WECHAT_WEB_LOGIN } from '../constants/wechatWebLogin';
import { captureMerchantPromoFromSearch, getMerchantPromoCode } from '../utils/merchantPromoRef';

const { Title, Text } = Typography;

/**
 * 系统默认入口：原首页全屏体验（视频英雄区 + 导航 + 联系表单 + 快速工作台）
 * 与租户登录表单同页展示。
 */
const MainLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, token } = useAuth();
  const [wechatWebEnabled, setWechatWebEnabled] = useState(false);
  const [wechatBindModalOpen, setWechatBindModalOpen] = useState(false);
  const [wechatWebBindToken, setWechatWebBindToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [isWorkbenchOpen, setIsWorkbenchOpen] = useState(false);
  const [heroVideoBroken, setHeroVideoBroken] = useState(false);
  const [teamPickOpen, setTeamPickOpen] = useState(false);
  const [pickCandidates, setPickCandidates] = useState<TeamPickCandidate[]>([]);
  const [pickLoading, setPickLoading] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [registerForm] = Form.useForm();
  const [codeSeconds, setCodeSeconds] = useState(0);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [form] = Form.useForm();
  const [authCardCollapsed, setAuthCardCollapsed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const wechatOauthHandledRef = useRef(false);

  useEffect(() => {
    if (codeSeconds <= 0) return undefined;
    const t = window.setTimeout(() => setCodeSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [codeSeconds]);

  useEffect(() => {
    captureMerchantPromoFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          video.style.pointerEvents = 'none';
        })
        .catch(() => {
          const handleInteraction = () => {
            video.play().catch(() => {});
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('click', handleInteraction);
          };
          document.addEventListener('touchstart', handleInteraction, { once: true });
          document.addEventListener('click', handleInteraction, { once: true });
        });
    }
  }, []);

  useEffect(() => {
    const saved = loadSavedTenantLogin();
    if (saved) {
      form.setFieldsValue({
        teamCode: saved.teamCode || undefined,
        username: saved.username,
        password: saved.password,
        rememberPassword: true
      });
    }
  }, [form]);

  useEffect(() => {
    if (!ENABLE_WECHAT_WEB_LOGIN) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth/wechat-web/config');
        const data = (await res.json().catch(() => ({}))) as { enabled?: boolean };
        if (!cancelled && data.enabled) {
          setWechatWebEnabled(true);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const completeLoginWithSessionToken = useCallback(
    async (sessionToken: string) => {
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        if (!res.ok) {
          message.error('登录状态异常，请重试');
          return;
        }
        const data = (await res.json()) as Record<string, unknown>;
        const team = data.team as { name?: string; code?: string } | undefined;
        const codeForStorage =
          String(team?.code || '')
            .trim()
            .toUpperCase() || undefined;
        login(
          sessionToken,
          data.role as string,
          data.username as string,
          data.name as string | undefined,
          data.avatar as string | undefined,
          data.isFirstLogin as boolean | undefined,
          data.passwordChanged as boolean | undefined,
          data._id as string | undefined,
          data.teamId as string | undefined,
          codeForStorage,
          team?.name
        );
        message.success('登录成功');
        const st = location.state as { from?: { pathname?: string; search?: string } } | null;
        navigate(resolvePostLoginPath({ role: data.role as string, from: st?.from }), { replace: true });
      } catch {
        message.error('网络异常，请检查连接后重试');
      }
    },
    [login, navigate, location.state]
  );

  useEffect(() => {
    if (!ENABLE_WECHAT_WEB_LOGIN) return;
    const params = new URLSearchParams(location.search);
    const oauth = params.get('wechat_oauth');
    if (!oauth) {
      wechatOauthHandledRef.current = false;
      return;
    }
    if (wechatOauthHandledRef.current) return;
    wechatOauthHandledRef.current = true;
    if (oauth === 'success') {
      const t = params.get('token');
      if (t) {
        void completeLoginWithSessionToken(decodeURIComponent(t));
      }
      window.history.replaceState({}, '', '/');
      return;
    }
    if (oauth === 'bind_required') {
      const tok = params.get('wechat_web_bind_token');
      if (tok) {
        setWechatWebBindToken(decodeURIComponent(tok));
        setWechatBindModalOpen(true);
        setAuthCardCollapsed(false);
      }
      window.history.replaceState({}, '', '/');
      return;
    }
    if (oauth === 'error') {
      message.error(params.get('msg') || '微信登录失败');
      window.history.replaceState({}, '', '/');
    }
  }, [location.search, completeLoginWithSessionToken, ENABLE_WECHAT_WEB_LOGIN]);

  const finishWithResult = (
    result: { ok: true; token: string; user: Record<string, unknown>; teamCode?: string; teamName?: string },
    values: { teamCode?: string; username: string }
  ) => {
    const u = result.user;
    const codeForStorage =
      String(result.teamCode || values.teamCode || '')
        .trim()
        .toUpperCase() || undefined;

    const rememberPassword = form.getFieldValue('rememberPassword') as boolean | undefined;
    const password = (form.getFieldValue('password') as string) || '';
    persistTenantLoginIfRemembered(!!rememberPassword, {
      teamCode: String(values.teamCode ?? form.getFieldValue('teamCode') ?? '').trim() || undefined,
      username: values.username,
      password
    });

    login(
      result.token,
      u.role as string,
      u.username as string,
      u.name as string | undefined,
      u.avatar as string | undefined,
      u.isFirstLogin as boolean | undefined,
      u.passwordChanged as boolean | undefined,
      u._id as string | undefined,
      u.teamId as string | undefined,
      codeForStorage,
      result.teamName
    );
    message.success('登录成功');
    const st = location.state as { from?: { pathname?: string; search?: string } } | null;
    navigate(resolvePostLoginPath({ role: u.role as string, from: st?.from }), { replace: true });
  };

  const finishWechatBindWithResult = (
    result: TenantLoginSuccess,
    values: { teamCode?: string; username: string }
  ) => {
    const u = result.user;
    const codeForStorage =
      String(result.teamCode || values.teamCode || '')
        .trim()
        .toUpperCase() || undefined;
    login(
      result.token,
      u.role as string,
      u.username as string,
      u.name as string | undefined,
      u.avatar as string | undefined,
      u.isFirstLogin as boolean | undefined,
      u.passwordChanged as boolean | undefined,
      u._id as string | undefined,
      u.teamId as string | undefined,
      codeForStorage,
      result.teamName
    );
    message.success('绑定并登录成功');
    const st = location.state as { from?: { pathname?: string; search?: string } } | null;
    navigate(resolvePostLoginPath({ role: u.role as string, from: st?.from }), { replace: true });
  };

  const onFinish = async (values: { teamCode?: string; username: string; password: string }) => {
    setLoading(true);
    try {
      const result = await performTenantLogin(values.teamCode || '', values.username, values.password);
      if (isTenantTeamPick(result)) {
        setPickCandidates(result.candidates);
        setTeamPickOpen(true);
        message.warning(result.error);
        return;
      }
      if (!result.ok) {
        message.error(result.error);
        return;
      }
      finishWithResult(result, values);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRegisterCode = async () => {
    let email: string;
    try {
      email = await registerForm.validateFields(['email']).then(v => String(v.email).trim());
    } catch {
      return;
    }
    setSendCodeLoading(true);
    try {
      const res = await fetch('/api/auth/register/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string; devBypass?: boolean };
      if (!res.ok) {
        message.error(data.error || '发送失败');
        return;
      }
      if (data.devBypass) {
        message.info(data.message || '开发模式请使用验证码 000000');
      } else {
        message.success(data.message || '验证码已发送');
      }
      setCodeSeconds(60);
    } finally {
      setSendCodeLoading(false);
    }
  };

  const onRegisterFinish = async (values: {
    email: string;
    code: string;
    username: string;
    password: string;
  }) => {
    setRegisterLoading(true);
    try {
      const res = await fetch('/api/auth/register/self', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: String(values.email).trim(),
          code: String(values.code).trim(),
          username: String(values.username).trim(),
          password: values.password,
          promoCode: getMerchantPromoCode() || undefined
        })
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      if (!res.ok) {
        message.error(data.error || '注册失败');
        return;
      }
      message.success(data.message || '注册成功');
      registerForm.resetFields();
      setAuthTab('login');
      form.setFieldsValue({ username: String(values.username).trim() });
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleTeamPickConfirm = async (userId: string) => {
    const teamCode = String(form.getFieldValue('teamCode') || '');
    const username = form.getFieldValue('username') as string;
    const password = form.getFieldValue('password') as string;
    setPickLoading(true);
    try {
      const result = await performTenantLogin(teamCode, username, password, userId);
      if (!result.ok || isTenantTeamPick(result)) {
        message.error(isTenantTeamPick(result) ? result.error : result.error);
        if (isTenantTeamPick(result)) {
          setPickCandidates(result.candidates);
        }
        return;
      }
      setTeamPickOpen(false);
      finishWithResult(result, { teamCode, username });
    } finally {
      setPickLoading(false);
    }
  };

  const handleEnterSystem = () => {
    if (token) {
      setIsWorkbenchOpen(true);
    } else {
      setAuthCardCollapsed(false);
      document.getElementById('home-login-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleContactUs = () => setIsContactFormOpen(true);

  return (
    <div className="homepage-container">
      <div className="hero-section">
        <video
          ref={videoRef}
          className="video-background"
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          onError={() => setHeroVideoBroken(true)}
          style={
            {
              pointerEvents: 'none',
              display: heroVideoBroken ? 'none' : undefined
            } as React.CSSProperties
          }
          {...({
            // DOM/微信内核等：非标准属性需字符串，布尔会触发 React 警告
            'webkit-playsinline': 'true',
            'x5-playsinline': 'true',
            'x5-video-player-type': 'h5',
            'x5-video-player-fullscreen': 'true'
          } as Record<string, string>)}
        >
          <source src={HERO_BACKGROUND_MP4} type="video/mp4" />
          您的浏览器不支持视频播放。
        </video>

        <div className="video-overlay" />

        <nav className="homepage-nav">
          <div className="nav-links">
            <button type="button" className="nav-link active">
              首页
            </button>
            <button type="button" className="nav-link" onClick={handleContactUs}>
              联系我们
            </button>
          </div>
        </nav>

        <div className="homepage-main-content">
          <div className="homepage-main-inner">
            <div className="content-center homepage-hero-text">
              <h1 className="main-headline">LabMind实验室管理系统</h1>
              <p className="sub-headline">高效、智能的实验室资源管理平台</p>
              <button type="button" className="cta-button" onClick={handleEnterSystem}>
                {token ? '进入系统' : '登录 / 注册'}
              </button>
            </div>

            <div className="home-login-card-wrap" id="home-login-panel">
              {!token && !authCardCollapsed ? (
              <Card className="home-login-card home-login-card--precision" variant="borderless">
                  <div className="precision-auth-surface home-precision-auth-surface">
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      className="login-close-btn home-auth-close-btn"
                      aria-label="关闭登录区域"
                      onClick={() => setAuthCardCollapsed(true)}
                    />
                    <div className="login-header--precision home-login-auth-header">
                      <Title level={3} className="login-headline home-labmind-title">
                        LabMind实验室管理系统
                      </Title>
                      <Text className="login-lead">登录或注册（个人账号无需团队即可体验）</Text>
                    </div>

                    <Tabs
                      className="home-auth-tabs--precision"
                      activeKey={authTab}
                      onChange={setAuthTab}
                      centered
                      items={[
                        {
                          key: 'login',
                          label: '登录',
                          children: (
                            <>
                            <Form
                              form={form}
                              layout="vertical"
                              size="large"
                              className="login-form login-form--precision"
                              colon={false}
                              onFinish={onFinish}
                            >
                              <Form.Item
                                className="login-lm-field"
                                name="teamCode"
                                label={
                                  <div className="login-label-row">
                                    <span className="login-label-main">
                                      团队编码
                                      <Tooltip title="新用户或未分班组成员可不填；若已知所在实验室编码，填写后可定点进入该团队。若账号在多个团队有档案，登录后将提示您选择。">
                                        <QuestionCircleOutlined />
                                      </Tooltip>
                                    </span>
                                    <span className="login-label-optional">选填</span>
                                  </div>
                                }
                              >
                                <Input placeholder="例如 LAB-204-PX" autoComplete="organization" />
                              </Form.Item>
                              <Form.Item
                                className="login-lm-field"
                                name="username"
                                label={<span className="login-label-block">用户名 / 学号 / 邮箱</span>}
                                rules={[{ required: true, message: '请输入用户名、学号或邮箱' }]}
                              >
                                <Input placeholder="用户名、学号、工号或邮箱" autoComplete="username" />
                              </Form.Item>
                              <Form.Item
                                className="login-lm-field"
                                name="password"
                                label={<span className="login-label-block">密码</span>}
                                rules={[{ required: true, message: '请输入密码' }]}
                              >
                                <Input.Password placeholder="请输入密码" autoComplete="current-password" />
                              </Form.Item>
                              <div className="login-remember-forgot-row">
                                <Form.Item name="rememberPassword" valuePropName="checked" noStyle>
                                  <Checkbox>记住密码</Checkbox>
                                </Form.Item>
                                <Button type="link" onClick={() => navigate('/forgot-password')}>
                                  忘记密码
                                </Button>
                              </div>
                              <Form.Item className="login-button-container">
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                  loading={loading}
                                  block
                                  size="large"
                                  className="login-modal__submit"
                                  icon={loading ? undefined : <ArrowRightOutlined />}
                                  iconPosition="end"
                                >
                                  {loading ? '登录中...' : '登录'}
                                </Button>
                              </Form.Item>
                            </Form>
                            {ENABLE_WECHAT_WEB_LOGIN && wechatWebEnabled ? (
                              <>
                                <div className="home-wechat-login-divider" aria-hidden>
                                  <span className="home-wechat-login-divider__line" />
                                  <span className="home-wechat-login-divider__text">或</span>
                                  <span className="home-wechat-login-divider__line" />
                                </div>
                                <Button
                                  type="default"
                                  block
                                  size="large"
                                  className="home-wechat-login-btn"
                                  onClick={() => {
                                    window.location.href = '/api/auth/wechat-web/authorize';
                                  }}
                                >
                                  微信扫码登录
                                </Button>
                              </>
                            ) : null}
                            </>
                          )
                        },
                        {
                          key: 'register',
                          label: '注册',
                          children: (
                            <Form
                              form={registerForm}
                              layout="vertical"
                              size="large"
                              className="login-form login-form--precision"
                              colon={false}
                              onFinish={onRegisterFinish}
                            >
                              <Form.Item
                                className="login-lm-field"
                                name="email"
                                label={<span className="login-label-block">邮箱</span>}
                                rules={[
                                  { required: true, message: '请填写邮箱' },
                                  { type: 'email', message: '邮箱格式不正确' }
                                ]}
                              >
                                <Input placeholder="用于验证与登录" autoComplete="email" />
                              </Form.Item>
                              <Form.Item
                                className="login-lm-field"
                                label={<span className="login-label-block">验证码</span>}
                                required
                              >
                                <Space.Compact style={{ width: '100%' }}>
                                  <Form.Item
                                    name="code"
                                    noStyle
                                    rules={[{ required: true, message: '请输入验证码' }]}
                                  >
                                    <Input placeholder="6 位数字" maxLength={6} style={{ width: '100%' }} />
                                  </Form.Item>
                                  <Button
                                    type="default"
                                    className="home-register-send-code-btn"
                                    loading={sendCodeLoading}
                                    disabled={codeSeconds > 0}
                                    onClick={handleSendRegisterCode}
                                  >
                                    {codeSeconds > 0 ? `${codeSeconds}s` : '获取验证码'}
                                  </Button>
                                </Space.Compact>
                              </Form.Item>
                              <Form.Item
                                className="login-lm-field"
                                name="username"
                                label={<span className="login-label-block">用户名</span>}
                                rules={[
                                  { required: true, message: '请设置用户名' },
                                  { min: 2, message: '至少 2 个字符' }
                                ]}
                              >
                                <Input placeholder="登录用，至少 2 个字符" autoComplete="username" />
                              </Form.Item>
                              <Form.Item
                                className="login-lm-field"
                                name="password"
                                label={<span className="login-label-block">密码</span>}
                                rules={[
                                  { required: true, message: '请设置密码' },
                                  { min: 6, message: '至少 6 位' }
                                ]}
                              >
                                <Input.Password placeholder="至少 6 位" autoComplete="new-password" />
                              </Form.Item>
                              <Form.Item className="login-button-container">
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                  loading={registerLoading}
                                  block
                                  size="large"
                                  className="login-modal__submit"
                                  icon={registerLoading ? undefined : <ArrowRightOutlined />}
                                  iconPosition="end"
                                >
                                  {registerLoading ? '提交中...' : '注册'}
                                </Button>
                              </Form.Item>
                              <Text type="secondary" className="home-register-hint">
                                注册后为「演示团队」浏览模式；登录后可在顶栏右侧使用「加入团队」或「创建团队」进入真实实验室。
                              </Text>
                            </Form>
                          )
                        }
                      ]}
                    />

                    <div className="home-auth-card-footer">
                      <Button
                        type="link"
                        onClick={() =>
                          message.info(
                            '请先使用上方账号登录，登录后在顶栏「演示团队」右侧点击「加入团队」或「创建团队」即可。'
                          )
                        }
                      >
                        注册新团队（创建实验室）
                      </Button>
                    </div>
                  </div>
              </Card>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <ContactForm isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />

      <QuickWorkbenchModal isOpen={isWorkbenchOpen} onClose={() => setIsWorkbenchOpen(false)} />

      <TeamPickModal
        open={teamPickOpen}
        candidates={pickCandidates}
        confirmLoading={pickLoading}
        onCancel={() => setTeamPickOpen(false)}
        onConfirm={handleTeamPickConfirm}
      />

      {ENABLE_WECHAT_WEB_LOGIN ? (
        <WechatWebBindModal
          open={wechatBindModalOpen}
          bindToken={wechatWebBindToken}
          onClose={() => {
            setWechatBindModalOpen(false);
            setWechatWebBindToken('');
          }}
          onBindSuccess={finishWechatBindWithResult}
        />
      ) : null}

      <footer className="homepage-site-footer" role="contentinfo">
        <SiteBeianRecords />
      </footer>
    </div>
  );
};

export default MainLoginPage;
